'use client'

import { useState, useCallback } from 'react'
import { MigrationPromptBanner } from '@/document'
import { EntryUpdateProvider, PromptCaptureModal } from '@/capture'
import { EmbeddingProvider } from '@/search'
import { InboxProvider } from '@/social'
import { RightPanelProvider } from './RightPanelProvider'
import { ErrorBoundary } from './error-boundary'
import { GlobalSearchModal } from '@/search'
import {
  MobileDrawer,
  MobileTopBar,
  Sidebar,
  useNavigationShortcuts,
  useSidebarState,
} from '@/navigation'

/**
 * The composition behind `/app`: the provider stack, the two layouts, and the two modals.
 *
 * This was `app/app/layout-client.tsx`, which #316 took the keyboard layer out of and #317 moved
 * here whole. `app/app/layout.tsx` keeps the default export Next.js resolves, does the auth check
 * that has to happen on the server, and delegates (ADR 0010).
 *
 * The provider order is load-bearing and is not this file's to reorder:
 * `EntryUpdateProvider` > `EmbeddingProvider` > `InboxProvider` > `RightPanelProvider`.
 * [#111](https://github.com/cinjoff/konstantout/issues/111) is open on the stack shipping
 * `AutomergeAdapter` to all 13 routes.
 */
export interface ProtectedLayoutProps {
  children: React.ReactNode
  userId: string
}

export function ProtectedLayout({ children, userId }: ProtectedLayoutProps) {
  const [searchOpen, setSearchOpen] = useState(false)
  const [captureOpen, setCaptureOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { collapsed, toggle: toggleSidebar } = useSidebarState()

  const handleCaptureOpen = useCallback(() => setCaptureOpen(true), [])
  const handleSearchOpen = useCallback(() => setSearchOpen(true), [])
  const handleDrawerOpen = useCallback(() => setDrawerOpen(true), [])
  const handleDrawerClose = useCallback(() => setDrawerOpen(false), [])

  useNavigationShortcuts({
    onSearchOpen: handleSearchOpen,
    onCaptureOpen: handleCaptureOpen,
    onToggleSidebar: toggleSidebar,
  })

  return (
    <EntryUpdateProvider userId={userId}>
      <EmbeddingProvider>
        <InboxProvider>
          <RightPanelProvider>
            {/* Desktop layout: sidebar + main */}
            <div className="hidden h-screen w-full sm:flex">
              <Sidebar
                collapsed={collapsed}
                onToggle={toggleSidebar}
                onCaptureOpen={handleCaptureOpen}
                onSearchOpen={handleSearchOpen}
              />
              <main
                id="main-content"
                className="flex flex-1 flex-col overflow-y-auto"
              >
                <div className="mx-auto w-full max-w-3xl px-5 pt-4">
                  <MigrationPromptBanner />
                </div>
                <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-8 px-5 pt-8 pb-8">
                  <ErrorBoundary>{children}</ErrorBoundary>
                </div>
              </main>
            </div>

            {/* Mobile layout: top bar + full-width main */}
            <div className="flex h-screen flex-col sm:hidden">
              <MobileTopBar
                onMenuOpen={handleDrawerOpen}
                onSearchOpen={handleSearchOpen}
                onCaptureOpen={handleCaptureOpen}
              />
              <main
                id="main-content-mobile"
                className="flex flex-1 flex-col overflow-y-auto"
              >
                <div className="w-full px-3 pt-4">
                  <MigrationPromptBanner />
                </div>
                <div className="flex w-full flex-1 flex-col gap-6 px-3 pt-6 pb-8">
                  <ErrorBoundary>{children}</ErrorBoundary>
                </div>
              </main>
              <MobileDrawer open={drawerOpen} onClose={handleDrawerClose} />
            </div>

            <GlobalSearchModal open={searchOpen} onOpenChange={setSearchOpen} />
            <PromptCaptureModal
              open={captureOpen}
              onOpenChange={setCaptureOpen}
            />
          </RightPanelProvider>
        </InboxProvider>
      </EmbeddingProvider>
    </EntryUpdateProvider>
  )
}
